import React, { useEffect } from 'react';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import {
  resetPrintAgentShowTaxBreakdown,
  setPrintAgentShowTaxBreakdown,
  setPrintAgentTimeZone,
} from '../../services/printAgentService';
import { setBluetoothPrintTimeZone } from '../../services/bluetoothPrintService';

/**
 * Cada cuánto se vuelve a pedir `MostrarDesgloseIVA`. Esta suscripción vive toda la sesión
 * y nada más la refresca (no hay refetchOnFocus ni evento en tiempo real para settings):
 * sin esto, un cambio hecho desde otro equipo no llegaba a esta caja hasta recargar, y los
 * tickets que arma el agente sin servidor seguían con el valor viejo mientras los del
 * servidor ya traían el nuevo.
 */
const TAX_BREAKDOWN_REFRESH_MS = 10 * 60_000;

/**
 * Pushes the restaurant settings the tickets depend on into the module-level
 * print services: the timezone (wall-clock timestamps in the restaurant's tz,
 * not the device's) and `MostrarDesgloseIVA` (for the ticket the Print Agent
 * builds on its own when the server doesn't answer).
 *
 * The print services are not React components — they're called from event
 * handlers (cobrar, pre-cuenta, comanda, arqueo). They can't read the hooks
 * directly, so this provider runs once on mount and again any time settings
 * change (or, for MostrarDesgloseIVA, every TAX_BREAKDOWN_REFRESH_MS), keeping
 * the singletons aligned with the active tenant.
 */
const PrintServicesSettingsSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { timeZone, isLoading } = useRestaurantTimeZone();
  const { data: showTaxBreakdownSetting } = useGetBoolSettingQuery('MostrarDesgloseIVA', {
    pollingInterval: TAX_BREAKDOWN_REFRESH_MS,
  });

  useEffect(() => {
    if (isLoading) return;
    setPrintAgentTimeZone(timeZone);
    setBluetoothPrintTimeZone(timeZone);
  }, [timeZone, isLoading]);

  // Sin respuesta (cargando, o sin red y sin copia guardada) se queda el último valor
  // conocido en vez de volver al default.
  const showTaxBreakdown = showTaxBreakdownSetting?.value;
  useEffect(() => {
    if (showTaxBreakdown === undefined) return;
    setPrintAgentShowTaxBreakdown(showTaxBreakdown);
  }, [showTaxBreakdown]);

  // Pero "último valor conocido" es de esta sesión: al desmontar (cerrar sesión, sin
  // recargar la pestaña) vuelve al default, para no imprimir con el de otro restaurante.
  useEffect(() => resetPrintAgentShowTaxBreakdown, []);

  return <>{children}</>;
};

export default PrintServicesSettingsSync;
